<template>
	<Popper
		v-click-outside="closePopover"
		class="absolute bottom-0 right-0 mb-24 mr-24 z-10"
		:isVisible="popoverVisible"
		placement="top-end"
		@keyup.escape="closePopover"
	>
		<button data-test="help-button" class="help-button dark" @click="onHelpButtonClick">
			<BaseIcon v-if="popoverVisible" name="mono/delete" class="w-12 h-12" />
			<BaseIcon v-else name="mono/help" class="mt-1" />
		</button>
		<template #popup>
			<BasePopover data-test="help-menu" style="width: 160px">
				<a href="https://docs.birdeatsbug.com" class="popover-link" target="_blank" rel="noreferrer">
					Help guides
				</a>
				<RouterLink to="/about" data-test="about-link" class="popover-link">About</RouterLink>
				<hr class="border-gray-600 my-4" />
				<p class="popover-heading">GET SUPPORT</p>
				<a href="https://status.birdeatsbug.com/" class="popover-link" target="_blank" rel="noreferrer">
					System status
				</a>

				<template v-if="isCompatibleWithSdk">
					<button class="popover-link" @click="contactUs">Contact us</button>
					<button class="popover-link" @click="reportBug">Report a Bug</button>
				</template>
			</BasePopover>
		</template>
	</Popper>
</template>

<script>
import Popper from 'shared/components/Popper.vue'
import analytics from 'shared/utils/analytics'
import initializeSdk, {
	sharedSdkUiOptions,
	defaultPublicAppId,
	setIsEmailRequiredForBugReports,
} from 'shared/utils/birdSdk'

export default {
	components: {
		Popper,
	},
	props: {
		inSessionList: Boolean,
	},
	data() {
		return {
			popoverVisible: false,
			isCompatibleWithSdk: true,
			prevRoute: null,
		}
	},
	watch: {
		popoverVisible(isVisible) {
			if (!isVisible) return
			this.initializeSdk()
		},
	},
	methods: {
		closePopover() {
			this.popoverVisible = false
		},
		onHelpButtonClick() {
			this.popoverVisible = !this.popoverVisible
			analytics.track('Help Button Clicked')
		},
		initializeSdk(options) {
			const sdkOptions = {
				...options,
				hooks: {
					afterInit: ({isBrowserSupported}) => (this.isCompatibleWithSdk = isBrowserSupported),
				},
			}
			initializeSdk(sdkOptions)
			setIsEmailRequiredForBugReports(this.$auth.user.value?.email)
		},
		contactUs() {
			const options = {
				publicAppId: defaultPublicAppId,
				ui: {
					...sharedSdkUiOptions,
					theme: this.$store.theme,
				},
			}
			if (sessionStorage) sessionStorage.sdkOptions = JSON.stringify(options)
			if (!window.birdeatsbug) {
				this.initializeSdk(options)
			} else {
				window.birdeatsbug.setOptions(options)
			}
			window.birdeatsbug.trigger()
			analytics.track('"Contact Us" Clicked In Help Menu')
		},
		reportBug() {
			const options = {
				publicAppId: 'dcd41f9d-3432-4fac-81ad-1b39d8fc2cc4', // this ID is setup on all environments
				ui: {
					...sharedSdkUiOptions,
					theme: this.$store.theme,
					text: {
						previewScreen: {
							title: 'Report a Bug',
							descriptionInputPlaceholder: 'Please describe the issue you are experiencing',
						},
					},
				},
			}
			if (sessionStorage) sessionStorage.sdkOptions = JSON.stringify(options)
			if (!window.birdeatsbug) {
				this.initializeSdk(options)
			} else {
				window.birdeatsbug.setOptions(options)
			}
			window.birdeatsbug.trigger()
			analytics.track('"Report A Bug" Clicked In Help Menu')
		},
	},
}
</script>

<style scoped>
.help-button {
	height: 36px;
	width: 36px;
	z-index: 100;
	background-color: var(--gray-600);
	border: 1px solid var(--gray-500);
	border-radius: 100%;
	display: inline-flex;
	justify-content: center;
	align-items: center;
	color: #fff;
}

.help-button:hover {
	background-color: color-mix(in srgb, var(--gray-600), #fff 10%);
	filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5));
}

.help-button:focus-visible {
	box-shadow: inset 0 0 0 2px var(--blue-700);
}

.popover-link {
	color: var(--gray-100);
}
</style>

<style>
#birdeatsbug-sdk.dark {
	--gray-500: #4b566b;
	--gray-600: #353d4d;
	--gray-700: #2d3443;

	--screen-bg-color: var(--gray-700);
	--secondary-bg-color: var(--gray-700);
	--border-color: var(--gray-600);
	--form-input-focus-border-color: var(--gray-500);
	--form-input-bg-color: var(--gray-600);
}
</style>
